// useNodeContributions.js

import { useCallback, useRef } from 'react';

import { toast } from 'react-toastify';

import { SaveMode } from 'utilities/Enums';
import { createNode } from 'utilities/NodeFactory';
import { getSingularLabel } from 'utilities/Languages';
import { getResolvedFields } from 'utilities/NodeType';

import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useBase } from 'context/BaseContext';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { uploadService } from 'services/nodes/NodeUploadService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { registerProvisionalSession } from 'node/provisional/provisionalNodeSession';
import { applyWorkflowChecklistItemKey } from 'node/missions/mission/utils/workflowStepAttachmentUtils';

export default function useNodeContributions({
  node,
  user,
  navigate: _navigate,
  setCurrentNode: _setCurrentNode,
  setCurrentSection: _setCurrentSection,
  contributeParent,
  setContributeOpen,
  setContributeParent,
  setPreSelectedType,
  setCameraOpen,
  setCameraContentType,
  setDefaultNode,
  setEditorSaveMode,
  setInitialChildren,
  setInitialActiveNodeKey,
  setIsUploading,
  photoInputRef,
  videoInputRef,
  documentInputRef,
  filesInputRef,
  onQuickAddRequest,
  onRelatedNodeCreated,
  getChildCreateFields,
}) {
  const { getType } = useNodeTypes();
  const { activeBase } = useBase();
  const language = useCurrentLanguage();
  // Blocks duplicate create+open while createNodeDoc is in flight.
  const quickActionCreateInFlightRef = useRef(false);
  const { openNode } = useNodeOpenIntent();
  const queueContributionFilesToNode = useCallback((files, targetNode) => {
    if (!files || files.length === 0) return;
    if (!targetNode?.key || !user?.key) return;

    setIsUploading(true);

    const childFields = typeof getChildCreateFields === 'function'
      ? getChildCreateFields()
      : null;
    const uploadId = uploadService.queueContribution(
      files,
      targetNode,
      user,
      (uploadedItems) => {
        void uploadedItems;
        setIsUploading(false);
      },
      (error) => {
        console.error('Background contribution failed:', error);
        toast.error(error?.message || 'Contribution upload failed.');
        setIsUploading(false);
      },
      childFields ? { childFields } : undefined,
    );

    if (uploadId === null) {
      setIsUploading(false);
    }
  }, [getChildCreateFields, setIsUploading, user]);

  const handleBackgroundContribution = useCallback((files) => {
    if (!files || files.length === 0) return;

    // Upload directly to the current node (no POST wrapper).
    queueContributionFilesToNode(files, node);
  }, [node, queueContributionFilesToNode]);

  const nodeToFile = useCallback((contributionNode) => {
    if (!contributionNode) return null;

    const fallbackType = contributionNode.mimeType || (contributionNode.type === 'PHOTO' ? 'image/jpeg' : contributionNode.type === 'VIDEO' ? 'video/webm' : undefined);
    const fallbackName = contributionNode.fileName || `${(contributionNode.type || 'capture').toLowerCase()}-${contributionNode.key || Date.now()}`;
    const baseFile = contributionNode.tempFile || contributionNode.localFile;
    const existingUri = contributionNode.localUri || contributionNode.url;
    const fileUri = existingUri || (baseFile && typeof URL !== 'undefined' ? URL.createObjectURL(baseFile) : null);

    if (baseFile) {
      if (baseFile instanceof File) {
        return Object.assign(baseFile, {
          uri: fileUri,
          localUri: fileUri,
          base64Data: contributionNode.base64Data || null,
        });
      }

      const fileWithMeta = {
        name: (typeof baseFile.name === 'string' && baseFile.name.length > 0) ? baseFile.name : fallbackName,
        type: (typeof baseFile.type === 'string' && baseFile.type.length > 0) ? baseFile.type : fallbackType,
        size: baseFile.size,
        lastModified: baseFile.lastModified,
        uri: fileUri,
        localUri: fileUri,
        base64Data: contributionNode.base64Data || null,
        localFile: baseFile,
      };

      if (!fileWithMeta.type && fallbackType) {
        fileWithMeta.type = fallbackType;
      }

      if (!fileWithMeta.name && fallbackName) {
        fileWithMeta.name = fallbackName;
      }

      return fileWithMeta;
    }

    if (fileUri) {
      return {
        name: fallbackName,
        type: fallbackType,
        uri: fileUri,
        localUri: fileUri,
        base64Data: contributionNode.base64Data || null,
      };
    }

    return null;
  }, []);

  const applyInitialFields = useCallback((targetNode, typeKey, initialFields) => {
    if (!targetNode || !initialFields) return;
    const def = getType(typeKey);
    if (!def) return;

    const resolveSectionKeyForField = (fieldKey) => {
      const fieldName = typeof fieldKey === 'string' ? fieldKey.trim() : '';
      if (!fieldName) return def?.dataSection || null;

      const schemaFields = Array.isArray(def?.schema?.fields) ? def.schema.fields : [];
      const resolvedFields = getResolvedFields(typeKey, { includeHidden: true });
      const allFields = [
        ...schemaFields,
        ...(Array.isArray(resolvedFields) ? resolvedFields : []),
      ];

      const fieldDef = allFields.find((field) => {
        const candidateName = field?.name || field?.key;
        return candidateName === fieldName;
      }) || null;

      const sourceTypeKey = String(fieldDef?.source || def?.key || typeKey || '').trim();
      if (!sourceTypeKey) return def?.dataSection || null;

      const sourceDef = getType(sourceTypeKey);
      if (sourceDef?.dataSection) return sourceDef.dataSection;

      const normalizedSource = sourceTypeKey.toLowerCase();
      return normalizedSource ? `data_${normalizedSource}` : (def?.dataSection || null);
    };

    const containersBySection = new Map();

    Object.entries(initialFields).forEach(([fieldKey, value]) => {
      if (value === undefined) return;

      const sectionKey = resolveSectionKeyForField(fieldKey);
      if (!sectionKey) return;

      const existing = containersBySection.get(sectionKey)
        || ((targetNode[sectionKey] && typeof targetNode[sectionKey] === 'object')
          ? { ...targetNode[sectionKey] }
          : {});

      existing[fieldKey] = value;
      containersBySection.set(sectionKey, existing);
    });

    containersBySection.forEach((container, sectionKey) => {
      if (Object.keys(container).length > 0) {
        targetNode[sectionKey] = container;
      }
    });
  }, [getType]);

  const applyParentFields = useCallback((targetNode, parentNode) => {
    if (!targetNode || !parentNode?.key) return;

    targetNode.parentTitle = parentNode.title || '';
    targetNode.parentType = parentNode.type || null;
    targetNode.profileRoot = false;

    const stamped = stampExclusiveAncestry(
      targetNode,
      parentNode,
      getNodeType(targetNode.type),
    );
    if (Array.isArray(stamped?.ancestryPath)) {
      targetNode.ancestryPath = stamped.ancestryPath;
    }
  }, []);

  const handleNodeCreated = useCallback(async (newNodes) => {
    if (!newNodes || newNodes.length === 0) return;

    const contributionTarget = contributeParent || node;

    const editorCandidate = newNodes.find((createdNode) => createdNode?.launchEditor);

    if (editorCandidate) {
      const remainingChildren = newNodes.filter((child) => child && child !== editorCandidate);

      // Open editor on the created node itself. Any additional created items
      // should be attached directly to the contribution target separately.
      setDefaultNode(editorCandidate);
      setInitialChildren([]);
      setInitialActiveNodeKey(null);

      setEditorSaveMode(SaveMode.CREATE);
      // Inline view is always visible.

      // Close contribution UI immediately; persistence happens only on editor Save.
      setContributeOpen(false);
      setContributeParent(null);
      setPreSelectedType(null);

      // If the action created additional items (e.g. multi-file selection),
      // upload/persist them directly under the contribution target.
      if (remainingChildren.length > 0 && contributionTarget?.key) {
        const contributionNodes = remainingChildren.filter((n) => !n?.launchEditor);
        const files = contributionNodes
          .map(nodeToFile)
          .filter(Boolean);

        if (files.length > 0) {
          queueContributionFilesToNode(files, contributionTarget);
        }
      }

      return;
    }

    const contributionNodes = newNodes.filter((n) => !n?.launchEditor);

    if (contributionNodes.length > 0) {
      const files = contributionNodes
        .map(nodeToFile)
        .filter(Boolean);

      if (files.length > 0) {
        if (contributionTarget?.key) {
          queueContributionFilesToNode(files, contributionTarget);
        } else {
          handleBackgroundContribution(files);
        }
      }
    }

    setContributeOpen(false);
    setContributeParent(null);
    setPreSelectedType(null);
  }, [
    contributeParent,
    node,
    nodeToFile,
    queueContributionFilesToNode,
    handleBackgroundContribution,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setDefaultNode,
    setEditorSaveMode,
    setInitialChildren,
    setInitialActiveNodeKey,
  ]);

  const handleQuickActionSelect = useCallback(async (actionKey, options = {}) => {
    const initialFields = options?.initialFields || null;
    const useQuickAdd = options?.quickAdd === true;
    const quickAddConfig = options?.quickAddConfig || null;
    const relatedNode = options?.relatedNode || node;
    // Handle media uploads specially - trigger file selection directly
    if (actionKey === 'PHOTO') {
      photoInputRef.current?.click();
      return;
    }

    if (actionKey === 'VIDEO') {
      videoInputRef.current?.click();
      return;
    }

    if (actionKey === 'DOCUMENT') {
      documentInputRef.current?.click();
      return;
    }

    if (actionKey === 'FILE' || actionKey === 'ATTACH') {
      filesInputRef.current?.click();
      return;
    }

    if (actionKey === 'CAMERA' || actionKey === 'SNAPSHOT' || actionKey === 'RECORDING') {
      // Avoid accidentally reusing an old contributeParent post.
      setContributeParent(null);
      setCameraContentType(actionKey === 'RECORDING' ? 'RECORDING' : 'SNAPSHOT');
      setCameraOpen(true);
      return;
    }

    // Handle MORE specially - open NodeAddModal without pre-selection
    if (actionKey === 'MORE') {
      setPreSelectedType(null); // No pre-selection for MORE
      setContributeParent(node);
      setContributeOpen(true);
      return;
    }

    // For other types, create the child node and open the editor on the child.
    if (!node?.key || !user?.key) return;

    if (useQuickAdd && typeof onQuickAddRequest === 'function') {
      onQuickAddRequest({
        typeKey: actionKey,
        initialFields,
        relatedNode,
        quickAddConfig,
      });
      return;
    }

    if (quickActionCreateInFlightRef.current) return;
    quickActionCreateInFlightRef.current = true;

    try {
      const newNode = await createNode(actionKey, user, activeBase?.key || null);

      const initialTitle = String(options?.initialTitle || '').trim();
      if (initialTitle) {
        newNode.title = initialTitle;
      } else if (!newNode.title || !String(newNode.title).trim()) {
        const typeDef = getType(newNode.type || actionKey);
        const singularLabel = getSingularLabel(typeDef, newNode.type || actionKey, language);
        newNode.title = `New ${singularLabel}`;
      }

      applyInitialFields(newNode, newNode.type || actionKey, initialFields);
      applyWorkflowChecklistItemKey(
        newNode,
        typeof getChildCreateFields === 'function' ? getChildCreateFields() : null,
      );

      // Attach directly to the current node.
      applyParentFields(newNode, node);
      newNode.contributorKey = user.key;

      // Create-then-Peek stubs: discard on abandon if the user never edits.
      const provisional = options?.provisional !== false;
      if (provisional) {
        newNode.provisional = true;
      }

      await createNodeDoc(newNode.key, newNode);

      if (provisional) {
        registerProvisionalSession(newNode);
      }

      // Open Peek as soon as the doc exists; link the parent edge in the background
      // so the linkNodes callable does not block the panel.
      if (typeof onRelatedNodeCreated === 'function') {
        onRelatedNodeCreated(newNode);
      } else {
        openNode(newNode, { preferPeek: true });
      }

      const edgeOptions = buildHierarchyEdgeOptions({
        parentNode: node,
        childNode: newNode,
        baseKey: activeBase?.key || node?.baseKey || newNode?.baseKey || null,
      });
      upsertEdge(node.key, newNode.key, edgeOptions).catch((edgeError) => {
        console.error('Failed to link created node:', edgeError);
        toast.error('Created, but failed to link to the parent.');
      });
    } catch (error) {
      console.error('Failed to create node:', error);
      toast.error(error?.message || 'Failed to create node.');
    } finally {
      quickActionCreateInFlightRef.current = false;
    }
  }, [
    photoInputRef,
    videoInputRef,
    documentInputRef,
    filesInputRef,
    setContributeParent,
    setCameraContentType,
    setCameraOpen,
    setPreSelectedType,
    node,
    setContributeOpen,
    user,
    openNode,
    applyInitialFields,
    applyParentFields,
    getType,
    language,
    activeBase?.key,
    onQuickAddRequest,
    onRelatedNodeCreated,
    getChildCreateFields,
  ]);

  const handleQuickActionCreateOnly = useCallback(async (actionKey, options = {}) => {
    const initialFields = options?.initialFields || null;
    const useQuickAdd = options?.quickAdd === true;
    const quickAddConfig = options?.quickAddConfig || null;
    const relatedNode = options?.relatedNode || node;
    // Handle media uploads specially - trigger file selection directly
    if (actionKey === 'PHOTO') {
      photoInputRef.current?.click();
      return;
    }

    if (actionKey === 'VIDEO') {
      videoInputRef.current?.click();
      return;
    }

    if (actionKey === 'DOCUMENT') {
      documentInputRef.current?.click();
      return;
    }

    if (actionKey === 'FILE' || actionKey === 'ATTACH') {
      filesInputRef.current?.click();
      return;
    }

    if (actionKey === 'CAMERA' || actionKey === 'SNAPSHOT' || actionKey === 'RECORDING') {
      // Avoid accidentally reusing an old contributeParent post.
      setContributeParent(null);
      setCameraContentType(actionKey === 'RECORDING' ? 'RECORDING' : 'SNAPSHOT');
      setCameraOpen(true);
      return;
    }

    // Handle MORE specially - open NodeAddModal without pre-selection
    if (actionKey === 'MORE') {
      setPreSelectedType(null); // No pre-selection for MORE
      setContributeParent(node);
      setContributeOpen(true);
      return;
    }

    // For other types, create the child node without navigating away.
    try {
      if (!node?.key || !user?.key) return;

      if (useQuickAdd && typeof onQuickAddRequest === 'function') {
        onQuickAddRequest({
          typeKey: actionKey,
          initialFields,
          relatedNode,
          quickAddConfig,
        });
        return;
      }

      const newNode = await createNode(actionKey, user, activeBase?.key || null);

      const initialTitle = String(options?.initialTitle || '').trim();
      if (initialTitle) {
        newNode.title = initialTitle;
      } else if (!newNode.title || !String(newNode.title).trim()) {
        const typeDef = getType(newNode.type || actionKey);
        const singularLabel = getSingularLabel(typeDef, newNode.type || actionKey, language);
        newNode.title = `New ${singularLabel}`;
      }

      applyInitialFields(newNode, newNode.type || actionKey, initialFields);
      applyWorkflowChecklistItemKey(
        newNode,
        typeof getChildCreateFields === 'function' ? getChildCreateFields() : null,
      );

      // Attach directly to the current node.
      applyParentFields(newNode, node);
      newNode.contributorKey = user.key;

      await createNodeDoc(newNode.key, newNode);
      await upsertEdge(
        node.key,
        newNode.key,
        buildHierarchyEdgeOptions({
          parentNode: node,
          childNode: newNode,
          baseKey: activeBase?.key || node?.baseKey || newNode?.baseKey || null,
        }),
      );
    } catch (error) {
      console.error('Failed to create node:', error);
    }
  }, [
    photoInputRef,
    videoInputRef,
    documentInputRef,
    filesInputRef,
    setContributeParent,
    setCameraContentType,
    setCameraOpen,
    setPreSelectedType,
    node,
    setContributeOpen,
    user,
    applyInitialFields,
    applyParentFields,
    getType,
    language,
    activeBase?.key,
    onQuickAddRequest,
    getChildCreateFields,
  ]);

  const handlePhotoSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const filesWithUri = files.map((file) =>
        Object.assign(file, {
          uri: URL.createObjectURL(file),
          localUri: URL.createObjectURL(file),
        })
      );
      // Bypass confirmation modal - upload immediately
      handleBackgroundContribution(filesWithUri);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = null;
  }, [handleBackgroundContribution]);

  const handleVideoSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const filesWithUri = files.map((file) =>
        Object.assign(file, {
          uri: URL.createObjectURL(file),
          localUri: URL.createObjectURL(file),
        })
      );
      // Bypass confirmation modal - upload immediately
      handleBackgroundContribution(filesWithUri);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = null;
  }, [handleBackgroundContribution]);

  const handleDocumentSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const filesWithUri = files.map((file) =>
        Object.assign(file, {
          uri: URL.createObjectURL(file),
          localUri: URL.createObjectURL(file),
        })
      );
      // Bypass confirmation modal - upload immediately
      handleBackgroundContribution(filesWithUri);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = null;
  }, [handleBackgroundContribution]);

  const handleFilesSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const filesWithUri = files.map((file) =>
        Object.assign(file, {
          uri: URL.createObjectURL(file),
          localUri: URL.createObjectURL(file),
        })
      );
      handleBackgroundContribution(filesWithUri);
    }
    e.target.value = null;
  }, [handleBackgroundContribution]);

  const handleCameraContentCreated = useCallback((capturedContent) => {
    const file = nodeToFile(capturedContent);

    setCameraOpen(false);
    setCameraContentType(null);

    if (!file) {
      console.warn('Camera capture did not produce an uploadable file.');
      return;
    }

    handleBackgroundContribution([file]);
  }, [handleBackgroundContribution, nodeToFile, setCameraContentType, setCameraOpen]);

  return {
    queueContributionFilesToNode,
    handleBackgroundContribution,
    handleNodeCreated,
    handleQuickActionSelect,
    handleQuickActionCreateOnly,
    handlePhotoSelect,
    handleVideoSelect,
    handleDocumentSelect,
    handleFilesSelect,
    handleCameraContentCreated,
  };
}
